import React from "react";
import type Stripe from "stripe";
import styled from "styled-components";

import { WorkspacePlanTier } from "common/interfaces";
import { Button } from "js/Components/Button";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import { themeColors } from "js/react/sharedStyles";
import ChangePaymentMethodDialog from "js/react/views/Billing/dialogs/ChangePaymentMethodDialog";
import UpdateBillingDetailsDialog from "js/react/views/Billing/dialogs/UpdateBillingDetailsDialog";

import { Divider } from "js/Components/Divider";
import getLogger, { LogGroup } from "js/core/logger";
import { FlexBox } from "js/react/components/LayoutGrid";
import { mediumText } from "js/textStyles";

import { BillingPromoCoupon, BillingPromoForm } from "../../../components/BillingPromoForm";
import { BlockContainer, BlockHeader, BlockTitle } from "./Containers";
import { CreditCardIcon } from "./CreditCardIcon";
import { LinkByStripeIcon } from "./LinkByStripeIcon";

export const logger = getLogger(LogGroup.BILLING);

const PaymentMethodContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    text-transform: capitalize;
    color: #666;
    font-size: 15px;
`;

const PaymentMethodInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
`;

const BillingDetailsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
`;

const CardInfoText = styled.div`
    color: #666;
    font-size: 15px;
`;

const BillingDetailsInfo = styled.div`
    ${mediumText}
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const LinkByStripeAccountContainer = styled.div`
    text-transform: none;
`;

function Link(props: { link: Stripe.PaymentMethod.Link }) {
    const { link } = props;

    const handleEditPaymentMethod = () => {
        ShowDialog(ChangePaymentMethodDialog);
    };

    return (<>
        <PaymentMethodInfoContainer>
            {link
                ? <>
                    <LinkByStripeIcon />
                    <LinkByStripeAccountContainer
                        data-test-id="payment-method-link-info">{link.email} </LinkByStripeAccountContainer>
                </>
                : <div data-test-id="payment-method-link-info">Link payment data missing</div>
            }
        </PaymentMethodInfoContainer>
        <Button unframed large color={themeColors.ui_blue} onClick={handleEditPaymentMethod}
            data-test-id="edit-payment-method-button">{link ? "edit" : "add"}</Button>
    </>);
}

function Card(props: { card: Stripe.PaymentMethod.Card, small?: boolean }) {
    const { card, small } = props;

    const handleEditPaymentMethod = () => {
        ShowDialog(ChangePaymentMethodDialog);
    };

    return (<FlexBox fillWidth horizontalAlign="between" verticalAlign="top">
        <PaymentMethodInfoContainer>
            <BlockHeader data-test-id="payment-method-block-header">Payment Method</BlockHeader>
            <BillingDetailsContainer>
                {card
                    ? <>
                        <CreditCardIcon small={small} card={card} />
                        <CardInfoText data-test-id="payment-method-card-info">****{card.last4}</CardInfoText>
                    </>
                    : <CardInfoText data-test-id="payment-method-card-info">Credit card data missing</CardInfoText>
                }
            </BillingDetailsContainer>
        </PaymentMethodInfoContainer>
        <Button unframed large color={themeColors.ui_blue} onClick={handleEditPaymentMethod}
            data-test-id="edit-payment-method-button">{card ? "update" : "add"}</Button>
    </FlexBox>);
}

const PastDueMessage = styled.div`
    font-size: 14px;
    margin-top: 5px;
    color: ${themeColors.warning};
    font-weight: 600;
    width: 100%;
    text-align: left;
`;

const PromoBlock = BillingController.withInitializedState(function PromoBlock(props: BillingControllerState) {
    const { stripeData: { upcomingInvoice } } = props;

    const verifyPromoCode = async (promoCode: Stripe.PromotionCode) => {
        await BillingController.applyPromotionCodeToSubscription(promoCode?.code);
    };

    // Verify the first discount with a coupon (there should be only one)
    const firstDiscount = upcomingInvoice?.discounts?.find((discount: Stripe.Discount) => discount?.coupon);

    if (firstDiscount && (typeof firstDiscount === "object")) {
        return (<BillingPromoCoupon coupon={firstDiscount.coupon} />);
    }

    return (<BillingPromoForm verifyPromoCode={verifyPromoCode} />);
});

export const PaymentMethodBlock = WorkspaceController.withInitializedState(BillingController.withInitializedState(
    function PaymentMethodBlock(props: WorkspaceControllerState & BillingControllerState) {
        const { stripeData: { subscription, paymentMethods, customer }, plan } = props;

        if (!plan.requiresSubscription) {
            return null;
        }

        const paymentMethod = subscription.default_payment_method ?? Object.values(paymentMethods)[0] ?? null;

        const address = customer.address ?? paymentMethod?.billing_details.address ?? null;

        const handleEditBillingDetails = () => {
            ShowDialog(UpdateBillingDetailsDialog);
        };

        if (plan.tier === WorkspacePlanTier.ENTERPRISE) {
            return null;
        }

        return (<BlockContainer data-test-id="payment-method-block">
            <BlockTitle data-test-id="payment-method-block-header">Payment details</BlockTitle>
            <PaymentMethodContainer>
                {paymentMethod.type === "link"
                    ? <Link link={paymentMethod?.link} />
                    : <Card small card={paymentMethod?.card} />}
            </PaymentMethodContainer>
            <Divider />
            <FlexBox fillWidth verticalAlign="top" horizontalAlign="between" gap={10}>
                <BillingDetailsInfo>
                    <BlockHeader data-test-id="billing-details-block-header">Billing Details</BlockHeader>
                    <div>
                        {customer.name && <div>{customer.name}</div>}
                        {address && <div>{[address.line1, address.line2, address.city, address.state, address.postal_code, address.country].filter(Boolean).join(", ")}</div>}
                        {customer.email && <div>{customer.email}</div>}
                    </div>
                </BillingDetailsInfo>
                <Button unframed large color={themeColors.ui_blue} onClick={handleEditBillingDetails}>edit</Button>
            </FlexBox>
            <Divider />
            <PromoBlock />
            {subscription.status === "past_due" && <PastDueMessage>Past due</PastDueMessage>}
        </BlockContainer>);
    },
));
